import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Delete as RevokeIcon, PersonAdd as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

// Operator-only — manages User.is_superadmin (site-wide admin), distinct
// from CompanyMember.role='admin' which is per-tenant. Both grant and
// revoke require typing a literal confirmation token; granting site
// admin gives full read/write across every tenant, so the friction is
// deliberate. Backend audits each action.
function SiteAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);

  // Grant dialog state
  const [grantDialog, setGrantDialog] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantConfirm, setGrantConfirm] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);

  // Revoke dialog state
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/operator/site-admins');
      setAdmins(data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load site admins.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/api/users');
      setUsers(data || []);
    } catch (_) { /* non-fatal */ }
  };

  useEffect(() => { refresh(); loadUsers(); }, []);

  const grant = async () => {
    setGrantBusy(true);
    setError(null);
    try {
      const target = users.find((u) => (u.email || '').toLowerCase() === grantEmail.toLowerCase());
      if (!target) throw new Error('No user found with that email.');
      await api.post('/api/operator/site-admins/grant', {
        user_id: target.id,
        confirmation: grantConfirm,
      });
      setGrantDialog(false);
      setGrantEmail('');
      setGrantConfirm('');
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Grant failed.');
    } finally {
      setGrantBusy(false);
    }
  };

  const revoke = async () => {
    setRevokeBusy(true);
    setError(null);
    try {
      await api.post('/api/operator/site-admins/revoke', {
        user_id: revokeTarget.id,
        confirmation: revokeConfirm,
      });
      setRevokeTarget(null);
      setRevokeConfirm('');
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Revoke failed.');
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Site Admins"
        subtitle="Users with full read/write across every tenant. Distinct from per-company admin."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setGrantDialog(true); setError(null); }}>
            Grant Site Admin
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Full name</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
              ) : admins.length === 0 ? (
                <TableRow><TableCell colSpan={5}>No site admins.</TableCell></TableRow>
              ) : admins.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.email}</TableCell>
                  <TableCell>{a.username}</TableCell>
                  <TableCell>{a.full_name || '—'}</TableCell>
                  <TableCell>{a.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke site admin">
                      <IconButton color="error" onClick={() => { setRevokeTarget(a); setRevokeConfirm(''); }}>
                        <RevokeIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Grant dialog */}
      <Dialog open={grantDialog} onClose={() => setGrantDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant site admin</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Site admin grants <strong>full read/write across every tenant</strong>.
            Use sparingly. The action is recorded in the audit log.
          </Alert>
          <TextField
            label="User email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="The user must already exist."
            autoFocus
          />
          <TextField
            label="Type GRANT to confirm"
            value={grantConfirm}
            onChange={(e) => setGrantConfirm(e.target.value)}
            fullWidth
            placeholder="GRANT"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialog(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={grant}
            disabled={grantBusy || grantConfirm !== 'GRANT' || !grantEmail}
          >
            {grantBusy ? 'Granting…' : 'Grant site admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Revoke site admin</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Revoke site admin from <strong>{revokeTarget?.email}</strong>?
            They retain any per-company memberships they have.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            The system will refuse if this is the last remaining site admin
            (to avoid locking the operator panel).
          </Alert>
          <TextField
            label="Type REVOKE to confirm"
            value={revokeConfirm}
            onChange={(e) => setRevokeConfirm(e.target.value)}
            fullWidth
            placeholder="REVOKE"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={revoke}
            disabled={revokeBusy || revokeConfirm !== 'REVOKE'}
          >
            {revokeBusy ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SiteAdmins;
